import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Dumbbell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/* Urban Sports Club (30.09.2026): Besuche je Monat, je Kurs und je Gast —
   Stammgäste sind die Kandidaten für eine eigene Mitgliedschaft; der CSV-Export
   dient dem Abgleich mit der USC-Auszahlung. */
function monate() {
  const d = new Date(); const out = [];
  for (let i = 0; i < 6; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
const MONATSNAME = (m) => new Date(`${m}-01T12:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

export default function UscPage() {
  const LISTE = monate();
  const [monat, setMonat] = useState(LISTE[0]);
  const [d, setD] = useState(null);
  const laden = useCallback(() => {
    api.get('/usc/uebersicht', { params: { monat } }).then(r => setD(r.data)).catch(() => toast.error('Übersicht nicht geladen'));
  }, [monat]);
  useEffect(() => { laden(); }, [laden]);
  const csv = async () => {
    try {
      const r = await api.get('/usc/export.csv', { params: { monat }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `usc-besuche-${monat}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
  };
  const kachel = (wert, label, unter) => (
    <Card><CardContent className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-extrabold tabular-nums">{wert}</div>
      {unter && <div className="text-[11px] text-muted-foreground">{unter}</div>}
    </CardContent></Card>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="usc-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Dumbbell className="w-6 h-6 text-marke" /> Urban Sports Club</h1>
          <p className="text-sm text-muted-foreground">Gäste über USC. Erfasst am Empfang („USC-Gast“), zählen nicht in die Mitglieder-Check-ins.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={monat} onChange={e => setMonat(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
            {LISTE.map(m => <option key={m} value={m}>{MONATSNAME(m)}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={csv}><Download className="w-4 h-4 mr-1" /> CSV für den Abgleich</Button>
        </div>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {kachel(d.besuche, 'Besuche')}
            {kachel(d.personen, 'Gäste')}
            {kachel(d.stammgaeste, 'Stammgäste', `ab ${d.stammgast_ab} Besuchen, noch ohne Mitgliedschaft`)}
            {kachel(d.umgewandelt, 'Mitglied geworden')}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="lg:col-span-1"><CardContent className="p-0">
              <div className="px-4 py-3 border-b border-border/60 text-sm font-bold">Je Kurs</div>
              {d.je_kurs.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Noch keine Besuche.</p> : (
                <table className="w-full text-sm"><tbody>
                  {d.je_kurs.map(k => <tr key={k.kurs} className="border-b border-border/40 last:border-0"><td className="px-4 py-2">{k.kurs}</td><td className="px-4 py-2 text-right tabular-nums font-bold">{k.besuche}</td></tr>)}
                </tbody></table>
              )}
            </CardContent></Card>
            <Card className="lg:col-span-2"><CardContent className="p-0">
              <div className="px-4 py-3 border-b border-border/60 text-sm font-bold">Je Gast</div>
              {d.je_person.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Noch keine Besuche.</p> : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
                    <th className="px-4 py-2 text-left font-semibold">Gast</th>
                    <th className="px-4 py-2 text-right font-semibold">im Monat</th>
                    <th className="px-4 py-2 text-right font-semibold">gesamt</th>
                    <th className="px-4 py-2 text-left font-semibold">Status</th>
                  </tr></thead>
                  <tbody>
                    {d.je_person.map(p => (
                      <tr key={p.lead_id} className="border-b border-border/40 last:border-0">
                        <td className="px-4 py-2"><Link to={`/leads/${p.lead_id}`} className="font-medium hover:underline">{p.name}</Link>{p.usc_id && <span className="text-xs text-muted-foreground"> · {p.usc_id}</span>}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{p.besuche}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-bold">{p.besuche_gesamt}</td>
                        <td className="px-4 py-2 text-xs">
                          {p.mitglied ? <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5">Mitglied</span>
                            : p.besuche_gesamt >= d.stammgast_ab ? <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">Stammgast · ansprechen</span>
                            : <span className="text-muted-foreground">Gast</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );
}
